import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Clock, DollarSign, Check, X, Loader2, 
  User, MapPin, Truck, CreditCard, Store, RefreshCw,
  AlertCircle, Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoPartsOrderApproval() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({ count: 0, total_value: 0 });

  const fetchPendingOrders = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/auto-parts/orders/pending-approval`, { headers, withCredentials: true });
      setPendingOrders(response.data.orders || []);
      setStats({
        count: response.data.count || 0,
        total_value: response.data.total_value || 0
      });
    } catch (error) {
      console.error('Failed to fetch pending orders:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchProviders = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/providers`, { headers, withCredentials: true });
      // Filter only online or delivery-capable providers
      const providerList = (response.data.providers || response.data || []).filter(p => 
        ['mechanic', 'roadside_tech', 'delivery_driver'].includes(p.user_type)
      );
      setProviders(providerList);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPendingOrders();
    fetchProviders();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchPendingOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingOrders, fetchProviders]);

  const handleApprove = async (order) => {
    setSelectedOrder(order);
    setAssignDialogOpen(true);
  };

  const confirmApproval = async () => {
    if (!selectedOrder) return;
    
    setProcessing(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      await axios.post(
        `${API}/auto-parts/orders/${selectedOrder.order_id}/approve`,
        { provider_id: selectedProvider || null },
        { headers, withCredentials: true }
      );
      
      toast.success('Order approved! Customer will be charged.');
      setAssignDialogOpen(false);
      setSelectedOrder(null);
      setSelectedProvider('');
      fetchPendingOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (orderId) => {
    if (!window.confirm('Are you sure you want to reject this order? The customer will NOT be charged.')) {
      return;
    }
    
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      await axios.post(
        `${API}/auto-parts/orders/${orderId}/reject`,
        { reason: 'Admin rejected' },
        { headers, withCredentials: true }
      );
      
      toast.success('Order rejected. Customer was not charged.');
      fetchPendingOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-primary">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold text-orange-600">{stats.count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-success">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Value</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_value)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Hold</p>
                <p className="text-sm font-medium text-blue-600">Customer NOT charged until you approve</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchPendingOrders} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Orders List */}
      {pendingOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No Pending Orders</h3>
            <p className="text-sm text-gray-400 mt-2">Auto parts orders requiring approval will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingOrders.map((order) => (
            <Card key={order.order_id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="bg-orange-50 border-b pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending Approval
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                  <div className="text-sm font-mono text-gray-500">
                    #{order.order_id.slice(-8).toUpperCase()}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Product Info */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Product</p>
                    <p className="font-medium text-sm">{order.product_name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {order.brand} • {order.category}
                    </p>
                    <Badge variant="outline" className="mt-2">
                      Qty: {order.quantity}
                    </Badge>
                  </div>
                  
                  {/* Customer Info */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Customer</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-sm">{order.customer_name}</span>
                    </div>
                    {order.customer_phone && (
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600">{order.customer_phone}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Store Info */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Pickup From</p>
                    <div className="flex items-center gap-2">
                      <Store className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-sm">{order.store_name}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                      <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{order.store_address}</span>
                    </p>
                  </div>
                  
                  {/* Delivery Address */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Deliver To</p>
                    <div className="flex items-start gap-2">
                      <Truck className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span className="text-sm">{order.delivery_address}</span>
                    </div>
                  </div>
                </div>
                
                {/* Pricing */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Items:</span>
                      <span className="ml-2 font-medium">{formatCurrency(order.items_total)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Delivery:</span>
                      <span className="ml-2">{formatCurrency(order.delivery_fee)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Platform:</span>
                      <span className="ml-2">{formatCurrency(order.platform_fee)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <span className="ml-2 font-bold text-green-600">{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm text-yellow-700 font-medium">
                      Customer will be charged {formatCurrency(order.total)} AFTER you approve
                    </span>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={() => handleApprove(order)}
                    className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                  >
                    <Check className="h-4 w-4" />
                    Approve & Assign Provider
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handleReject(order.order_id)}
                    className="flex-1 text-red-600 border-red-300 hover:bg-red-50 gap-2"
                  >
                    <X className="h-4 w-4" />
                    Reject Order
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Provider Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve & Assign Provider</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="font-medium">{selectedOrder.product_name}</p>
                <p className="text-sm text-gray-600">
                  {selectedOrder.store_name} → {selectedOrder.customer_name}
                </p>
                <p className="text-lg font-bold text-green-600 mt-2">
                  {formatCurrency(selectedOrder.total)}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Assign Provider (Optional)</label>
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider or leave empty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No assignment (pool)</SelectItem>
                    {providers.map((provider) => (
                      <SelectItem key={provider.user_id} value={provider.user_id}>
                        {provider.name} ({provider.user_type})
                        {provider.is_online && <Badge className="ml-2 bg-green-500">Online</Badge>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  If left empty, any available provider can claim this order
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Approving this order will immediately charge the customer's payment method.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmApproval}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Approve & Charge Customer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
